import React, { useEffect, useRef, useState } from "react"

import { cleanDocumentUrl, isImage, isPdf, isVideo } from "../../../Material/DocumentModal/utils"

// Open parameters the browser's built-in PDF viewer reads off the fragment. Chrome
// honours `view`; Firefox's pdf.js does not implement `view` and reads `zoom` instead,
// so both are sent. An unknown key is ignored rather than rejected.
const FIT_TO_PAGE = "view=Fit&zoom=page-fit"

/** Append the fit hint without clobbering a fragment the url already carries. */
function withFitToPage(url) {
    if (!url) return url
    const [base, fragment] = url.split("#")
    return fragment ? `${base}#${fragment}&${FIT_TO_PAGE}` : `${base}#${FIT_TO_PAGE}`
}

const ToolbarButton = ({ label, onClick, children }) => (
    <button type="button" className="idm-tool-btn" title={label} aria-label={label} onClick={onClick}>
        {children}
    </button>
)

const DocumentViewer = ({ url, fileName }) => {
    const [rotation, setRotation] = useState(0)
    const [scale, setScale] = useState(1)
    const [status, setStatus] = useState("loading")
    const imageRef = useRef(null)

    // Prev/Next swaps the url under the same component, so the status has to go back to
    // loading or the next document would render as though it were already there.
    useEffect(() => {
        setStatus("loading")
        setRotation(0)
        setScale(1)
    }, [url])

    // Rotation and zoom go to CSS as custom properties; the transform itself lives in
    // the stylesheet with everything else.
    useEffect(() => {
        if (imageRef.current) {
            imageRef.current.style.setProperty("--idm-image-rotation", `${rotation}deg`)
            imageRef.current.style.setProperty("--idm-image-scale", scale)
        }
    }, [rotation, scale, status])

    const displayUrl = cleanDocumentUrl(url)
    const renderAsImage = isImage(displayUrl, fileName)
    const renderAsVideo = isVideo(url, fileName)
    const renderAsPdf = isPdf(displayUrl, fileName)
    // Only these render something that reports when it has loaded.
    const hasPreview = renderAsImage || renderAsVideo || renderAsPdf

    return (
        <div className="idm-viewer">
            {status === "error" ? (
                <div className="idm-viewer-unsupported">
                    <p>Could not load this file.</p>
                    <a href={url} target="_blank" rel="noreferrer">Open or download it instead</a>
                </div>
            ) : (
                <>
                    {renderAsImage && (
                        <img
                            ref={imageRef}
                            className="idm-viewer-image"
                            src={displayUrl}
                            alt={fileName || "Document"}
                            onLoad={() => setStatus("ready")}
                            onError={() => setStatus("error")}
                        />
                    )}
                    {!renderAsImage && renderAsVideo && (
                        <video
                            className="idm-viewer-video"
                            src={displayUrl}
                            controls
                            playsInline
                            preload="metadata"
                            onLoadedMetadata={() => setStatus("ready")}
                            onError={() => setStatus("error")}
                        />
                    )}
                    {!renderAsImage && !renderAsVideo && renderAsPdf && (
                        <iframe
                            className="idm-viewer-frame"
                            src={withFitToPage(displayUrl)}
                            title="Document"
                            onLoad={() => setStatus("ready")}
                        />
                    )}
                    {!renderAsImage && !renderAsVideo && !renderAsPdf && (
                        <div className="idm-viewer-unsupported">
                            <p>Preview isn&rsquo;t available for this file type.</p>
                            <a href={url} target="_blank" rel="noreferrer">Open or download it instead</a>
                        </div>
                    )}
                </>
            )}

            {hasPreview && status === "loading" && (
                <div className="idm-viewer-loading" data-testid="idm-viewer-loading">
                    <span className="idm-spinner" aria-hidden="true" />
                    <span className="idm-viewer-loading-text">Loading document&hellip;</span>
                </div>
            )}

            {renderAsImage && status !== "error" && (
                // Rotate and zoom are image-only: a PDF is rendered by the browser's own
                // viewer inside the iframe, which brings its own controls and cannot be
                // driven from out here.
                <div className="idm-viewer-toolbar">
                    <ToolbarButton label="Zoom out" onClick={() => setScale(Math.max(0.3, scale - 0.1))}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="11" cy="11" r="7" /><path d="M20 20l-3.2-3.2" /><path d="M8 11h6" />
                        </svg>
                    </ToolbarButton>
                    <span className="idm-viewer-zoom">{Math.round(scale * 100)}%</span>
                    <ToolbarButton label="Zoom in" onClick={() => setScale(Math.min(4, scale + 0.1))}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="11" cy="11" r="7" /><path d="M20 20l-3.2-3.2" /><path d="M8 11h6" /><path d="M11 8v6" />
                        </svg>
                    </ToolbarButton>
                    <span className="idm-viewer-divider" />
                    <ToolbarButton label="Rotate" onClick={() => setRotation((rotation + 90) % 360)}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 12a9 9 0 1 1-3-6.7" /><path d="M21 3v5h-5" />
                        </svg>
                    </ToolbarButton>
                    <ToolbarButton label="Reset" onClick={() => { setRotation(0); setScale(1) }}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M8 3H5a2 2 0 0 0-2 2v3" /><path d="M16 3h3a2 2 0 0 1 2 2v3" />
                            <path d="M8 21H5a2 2 0 0 1-2-2v-3" /><path d="M16 21h3a2 2 0 0 0 2-2v-3" />
                        </svg>
                    </ToolbarButton>
                </div>
            )}
        </div>
    )
}

export default DocumentViewer
